import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
  SearchBox,
  Spinner,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add20Filled,
  ArrowSortDownLinesRegular,
  ArrowSortUpLinesRegular,
  ArrowSync20Regular,
} from "@fluentui/react-icons";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { Constants } from "common/constants";
import {
  useSharedCardGridStyles,
  useSharedEmptyStateStyles,
  useSharedPageContainerStyles,
  useSharedPageEmptyStateStyles,
  useSharedPageHeaderStyles,
  useSharedPageSearchStyles,
  useSharedPillButtonStyles,
  useSharedPillTabContainerStyles,
  useSharedResponsiveSectionStyles,
  useSharedSortControlStyles,
} from "theme/sharedStyles";
import { readSortingOptions, writeSortingOptions } from "utils/generalUtils";
import {
  createProject,
  deleteProject,
  exportProject,
  getProjectStatus,
  getProjects,
  updateProject,
} from "../../decoodaapi";
import { EditProjectDialog } from "./EditProjectDialog";
import { ProjectCard } from "./ProjectCard";
import { IProject } from "./projectCard.types";
import { ProjectStatus } from "./projectConstants";
import {
  DELETE_DIALOG,
  EMPTY_STATE_LABELS,
  PAGE_LABELS,
  SORT_FIELD_LABELS,
  SortField,
  Tab,
  TAB_LABEL_BY_VALUE,
  TAB_VALUES,
} from "./ProjectsPage.types";
import { ScoreProjectDialog } from "./ScoreProjectDialog";

const SORTING_SCOPE = "ProjectsCardView";

const ErrorFallback = (): JSX.Element => {
  const emptyStateStyles = useSharedPageEmptyStateStyles();

  return (
    <div className={emptyStateStyles.container}>
      <h2 className={emptyStateStyles.title}>Something went wrong</h2>
      <p className={emptyStateStyles.subtitle}>
        Please refresh the page or try again later.
      </p>
    </div>
  );
};

interface IErrorBoundaryState {
  hasError: boolean;
}

class ProjectErrorBoundary extends React.Component<
  { children: React.ReactNode },
  IErrorBoundaryState
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Project page error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback />;
    }
    return this.props.children;
  }
}

const ProjectsPageContent = (): JSX.Element => {
  const containerStyles = useSharedPageContainerStyles();
  const headerStyles = useSharedPageHeaderStyles();
  const sectionStyles = useSharedResponsiveSectionStyles();
  const pageSearchStyles = useSharedPageSearchStyles();
  const sortStyles = useSharedSortControlStyles();
  const gridStyles = useSharedCardGridStyles();
  const emptyStateStyles = useSharedEmptyStateStyles();
  const pillButtonStyles = useSharedPillButtonStyles();
  const pillTabContainerStyles = useSharedPillTabContainerStyles();
  const navigate = useNavigate();

  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const currentUserId = currentUser?.id;
  const isDecoodaAdmin = !!currentUser?.roles?.includes(
    Constants.DECOODA_ADMINS_GROUP_NAME_VALUE,
  );

  const newProjectButtonRef = useRef<HTMLButtonElement>(null);

  const [activeTab, setActiveTab] = useState<Tab>(TAB_VALUES.MY);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [projects, setProjects] = useState<IProject[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>(
    (readSortingOptions(SORTING_SCOPE)?.field as SortField) ?? "name",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    readSortingOptions(SORTING_SCOPE)?.order ?? "asc",
  );

  const tabValues = useMemo<Tab[]>(
    () =>
      isDecoodaAdmin
        ? [TAB_VALUES.MY, TAB_VALUES.SHARED, TAB_VALUES.ADMIN]
        : [TAB_VALUES.MY, TAB_VALUES.SHARED],
    [isDecoodaAdmin],
  );

  const effectiveActiveTab: Tab =
    !isDecoodaAdmin && activeTab === TAB_VALUES.ADMIN
      ? TAB_VALUES.MY
      : activeTab;

  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<IProject | undefined>(
    undefined,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [projectToDelete, setProjectToDelete] = useState<IProject | null>(null);
  const [showScoreDialog, setShowScoreDialog] = useState<boolean>(false);
  const [projectToScore, setProjectToScore] = useState<IProject | null>(null);

  const fetchProjects = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getProjects();
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    writeSortingOptions(SORTING_SCOPE, { field: sortField, order: sortOrder });
  }, [sortField, sortOrder]);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const projectsRef = useRef(projects);

  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollScoringProjects = useCallback(async () => {
    const scoringProjects = projectsRef.current.filter(
      (p) => p.status === ProjectStatus.InProgress,
    );

    if (scoringProjects.length === 0) {
      stopPolling();
      return;
    }

    for (const project of scoringProjects) {
      try {
        const response = await getProjectStatus({ projectId: project.id });
        if (response.status === ProjectStatus.Scored) {
          setProjects((prev) =>
            prev.map((p) =>
              p.id === project.id
                ? {
                    ...p,
                    status: ProjectStatus.Scored,
                    scoringMessage: response.message || undefined,
                  }
                : p,
            ),
          );
        } else if (response.status === ProjectStatus.Error) {
          setProjects((prev) =>
            prev.map((p) =>
              p.id === project.id
                ? {
                    ...p,
                    status: ProjectStatus.Error,
                    scoringMessage: response.message || "Scoring failed",
                  }
                : p,
            ),
          );
        }
      } catch (error) {
        console.error(`Error polling status for project ${project.id}:`, error);
      }
    }
  }, [stopPolling]);

  useEffect(() => {
    const hasScoringProjects = projects.some(
      (p) => p.status === ProjectStatus.InProgress,
    );

    if (hasScoringProjects && !pollingRef.current) {
      pollingRef.current = setInterval(pollScoringProjects, 5000);
    } else if (!hasScoringProjects && pollingRef.current) {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [projects, pollScoringProjects, stopPolling]);

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = tabValues.indexOf(effectiveActiveTab);
      let nextIndex: number | null = null;
      if (e.key === "ArrowRight")
        nextIndex = (currentIndex + 1) % tabValues.length;
      else if (e.key === "ArrowLeft")
        nextIndex = (currentIndex - 1 + tabValues.length) % tabValues.length;
      if (nextIndex !== null) {
        e.preventDefault();
        setActiveTab(tabValues[nextIndex]);
        tabRefs.current[nextIndex]?.focus();
      }
    },
    [effectiveActiveTab, tabValues],
  );

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      const isOwner = project.createdBy?.userId === currentUserId;
      const isCollaborator =
        !!currentUserId &&
        !isOwner &&
        project.access?.some((c) => c.userId === currentUserId);

      if (effectiveActiveTab === TAB_VALUES.MY && !isOwner) return false;
      if (effectiveActiveTab === TAB_VALUES.SHARED && !isCollaborator)
        return false;

      if (!debouncedSearchQuery.trim()) return true;

      const query = debouncedSearchQuery.toLowerCase();
      const nameMatches = project.name?.toLowerCase().includes(query);
      const objectiveMatches = project.objective?.toLowerCase().includes(query);
      const clientNameMatches = project.clientName
        ?.toLowerCase()
        .includes(query);
      const workdayCodeMatches = project.workdayCode
        ?.toLowerCase()
        .includes(query);
      const accessMatches = project.access?.some((c) =>
        c.displayName.toLowerCase().includes(query),
      );

      return (
        nameMatches ||
        objectiveMatches ||
        clientNameMatches ||
        workdayCodeMatches ||
        accessMatches
      );
    });
  }, [projects, debouncedSearchQuery, effectiveActiveTab, currentUserId]);

  const sortedProjects = useMemo(() => {
    const sorted = [...filteredProjects];
    const dir = sortOrder === "asc" ? 1 : -1;

    return sorted.sort((a, b) => {
      if (sortField === "updatedAt") {
        const dateA = new Date(a.modifiedAt || a.created || 0).getTime();
        const dateB = new Date(b.modifiedAt || b.created || 0).getTime();
        return dir * (dateA - dateB);
      }
      return (
        dir * a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
      );
    });
  }, [filteredProjects, sortField, sortOrder]);

  const handleCreateClick = () => {
    setSelectedProject(undefined);
    setIsEditDialogOpen(true);
  };

  const handleEdit = (project: IProject) => {
    setSelectedProject(project);
    setIsEditDialogOpen(true);
  };

  const handleScore = (project: IProject) => {
    setProjectToScore(project);
    setShowScoreDialog(true);
  };

  const handleScoreDialogDismiss = () => {
    setShowScoreDialog(false);
    setProjectToScore(null);
  };

  const updateProjectStatus = useCallback(
    (projectId: string, status: ProjectStatus) => {
      setProjects((prev) =>
        prev.map((p) => (p.id === projectId ? { ...p, status } : p)),
      );
    },
    [],
  );

  const handleScoringStarted = useCallback(() => {
    if (projectToScore) {
      updateProjectStatus(projectToScore.id, ProjectStatus.InProgress);
    }
  }, [projectToScore, updateProjectStatus]);

  const handleScoringComplete = useCallback(() => {
    if (projectToScore) {
      updateProjectStatus(projectToScore.id, ProjectStatus.Scored);
    }
  }, [projectToScore, updateProjectStatus]);

  const handleDelete = (project: IProject) => {
    setProjectToDelete(project);
    setShowDeleteDialog(true);
  };

  const onDeleteConfirmed = async (): Promise<void> => {
    if (!projectToDelete) return;

    try {
      await deleteProject({ projectId: projectToDelete.id });
    } catch (error) {
      console.error("Error deleting project:", error);
    } finally {
      setProjectToDelete(null);
      setShowDeleteDialog(false);
    }
    fetchProjects().catch(console.error);
  };

  const handleOpen = (project: IProject) => {
    navigate(`/ai-analytics/${project.id}`);
  };

  const handleExport = async (project: IProject) => {
    try {
      await exportProject({ projectId: project.id });
    } catch (error) {
      console.error("Error exporting project:", error);
    }
  };

  const handleSaveProject = async (
    projectData: Partial<IProject>,
    options?: { skipRefresh?: boolean },
  ): Promise<string | void> => {
    if (projectData.id) {
      await updateProject({
        projectId: projectData.id,
        name: projectData.name,
        objective: projectData.objective,
        clientName: projectData.clientName,
        workdayCode: projectData.workdayCode,
        access: projectData.access?.map((a) => ({
          userId: a.userId,
          displayName: a.displayName,
        })),
      });
      if (!options?.skipRefresh) {
        await fetchProjects();
      }
    } else {
      const result = await createProject({
        name: projectData.name || "",
        objective: projectData.objective,
        clientName: projectData.clientName,
        workdayCode: projectData.workdayCode,
        access:
          projectData.access?.map((a) => ({
            userId: a.userId,
            displayName: a.displayName,
          })) || [],
      });
      if (!options?.skipRefresh) {
        await fetchProjects();
      }
      return result.projectId;
    }
  };

  const handleDialogDismiss = (saved: boolean) => {
    setIsEditDialogOpen(false);
    setSelectedProject(undefined);
    if (saved) {
      fetchProjects().catch(console.error);
    }
    newProjectButtonRef.current?.focus();
  };

  return (
    <div className={containerStyles.container}>
      <div className={headerStyles.header}>
        <h1 className={headerStyles.title}>{PAGE_LABELS.TITLE}</h1>
        <Button
          ref={newProjectButtonRef}
          className={headerStyles.createButton}
          onClick={handleCreateClick}
          icon={<Add20Filled />}
        >
          {PAGE_LABELS.CREATE_PROJECT}
        </Button>
      </div>

      <div className={sectionStyles.root}>
        <SearchBox
          className={pageSearchStyles.searchInput}
          placeholder={PAGE_LABELS.SEARCH_PLACEHOLDER}
          aria-label="Search projects"
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data?.value || "")}
        />
      </div>

      <div className={sortStyles.container}>
        <div
          className={pillTabContainerStyles.tabList}
          role="tablist"
          aria-label="Project categories"
        >
          {tabValues.map((value, idx) => (
            <button
              key={value}
              type="button"
              ref={(el) => {
                tabRefs.current[idx] = el;
              }}
              id={`tab-${value}`}
              className={mergeClasses(
                pillButtonStyles.pillButton,
                effectiveActiveTab === value &&
                  pillButtonStyles.pillButtonActive,
              )}
              onClick={() => setActiveTab(value)}
              onKeyDown={handleTabKeyDown}
              role="tab"
              aria-selected={effectiveActiveTab === value}
              aria-controls="tabpanel-projects"
              tabIndex={effectiveActiveTab === value ? 0 : -1}
            >
              {TAB_LABEL_BY_VALUE[value]}
            </button>
          ))}
        </div>
        <div className={sortStyles.sortGroup}>
          <Tooltip
            content="Refresh project statuses"
            relationship="label"
            appearance="inverted"
          >
            <Button
              appearance="subtle"
              icon={<ArrowSync20Regular />}
              onClick={fetchProjects}
              aria-label="Refresh project statuses"
            />
          </Tooltip>
          <span id="sort-by-label-projects" className={sortStyles.sortLabel}>
            Sort by:
          </span>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <MenuButton
                appearance="outline"
                className={sortStyles.sortMenuButton}
                aria-labelledby="sort-by-label-projects"
              >
                {SORT_FIELD_LABELS[sortField]}
              </MenuButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem onClick={() => setSortField("name")}>Name</MenuItem>
                <MenuItem onClick={() => setSortField("updatedAt")}>
                  Date Updated
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <Tooltip
            content={
              sortOrder === "asc"
                ? "Currently ascending, sort descending"
                : "Currently descending, sort ascending"
            }
            relationship="label"
            appearance="inverted"
          >
            <Button
              appearance="subtle"
              icon={
                sortOrder === "asc" ? (
                  <ArrowSortDownLinesRegular />
                ) : (
                  <ArrowSortUpLinesRegular />
                )
              }
              className={sortStyles.sortDirectionButton}
              onClick={() =>
                setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
              }
              aria-label={
                sortOrder === "asc"
                  ? "Currently ascending, sort descending"
                  : "Currently descending, sort ascending"
              }
            />
          </Tooltip>
        </div>
      </div>

      <div
        id="tabpanel-projects"
        role="tabpanel"
        aria-labelledby={`tab-${effectiveActiveTab}`}
        className={gridStyles.gridScroll}
      >
        {isLoading ? (
          <div className={emptyStateStyles.container}>
            <Spinner size="large" label="Loading projects..." />
          </div>
        ) : sortedProjects.length > 0 ? (
          <div className={gridStyles.gridContainer}>
            <div className={gridStyles.grid}>
              {sortedProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onEdit={handleEdit}
                  onScore={handleScore}
                  onDelete={handleDelete}
                  onOpen={handleOpen}
                  onExport={handleExport}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={emptyStateStyles.container}>
            <Text size={300} weight="regular">
              {debouncedSearchQuery.trim()
                ? EMPTY_STATE_LABELS.NO_SEARCH_RESULTS
                : EMPTY_STATE_LABELS.NO_PROJECTS}
            </Text>
          </div>
        )}
      </div>

      {showDeleteDialog && (
        <ActionDialog
          isVisible={showDeleteDialog}
          title={DELETE_DIALOG.TITLE}
          onConfirmation={onDeleteConfirmed}
          onDismiss={() => setShowDeleteDialog(false)}
          confirmationMessage={DELETE_DIALOG.CONFIRM}
          dismissMessage={DELETE_DIALOG.CANCEL}
          message={`Are you sure you want to delete "${projectToDelete?.name}"? This will permanently remove the project and all its data.`}
        />
      )}

      {isEditDialogOpen && (
        <EditProjectDialog
          isOpen={isEditDialogOpen}
          project={selectedProject}
          onDismiss={handleDialogDismiss}
          onSave={handleSaveProject}
          existingProjects={projects}
        />
      )}

      {showScoreDialog && projectToScore && (
        <ScoreProjectDialog
          isOpen={showScoreDialog}
          project={projectToScore}
          onDismiss={handleScoreDialogDismiss}
          onScoringStarted={handleScoringStarted}
          onScoringComplete={handleScoringComplete}
        />
      )}
    </div>
  );
};

export const ProjectsPage = (): JSX.Element => (
  <ProjectErrorBoundary>
    <ProjectsPageContent />
  </ProjectErrorBoundary>
);
